/**
 * KinsCircle Phase 6A — AI Memory Service (frontend orchestrator).
 *
 * STRICTLY ADDITIVE. NEVER mutates family_graph_members, family_graph_memories,
 * board_posts, media, invites, profiles, households, or any storage object.
 * Every method:
 *   - early-returns when its feature flag is OFF
 *   - calls the `ai-memory-assistant` edge function via supabase.functions.invoke
 *   - applies a client-side rate-limit (per-action, per-tab) as a soft guard
 *   - normalizes errors into a safe, non-throwing envelope
 *   - keeps a small in-memory + localStorage usage log for the diagnostics card
 *
 * Output is ALWAYS advisory / draft-only. The user must explicitly approve
 * before anything is written to a database row by some other (non-AI) code path.
 */

import { supabase } from '@/lib/supabase';
import {
  isAIMemoryWriterEnabled,
  isAITimelineEnabled,
  isAIMemoryTagsEnabled,
  isAIOnThisDayEnabled,
} from './featureFlag';
import { checkAIQuota, recordAIQuotaUsage } from './aiQuota';
import type {
  AIAction,
  MemoryDraftInput,
  TimelineInput,
  OnThisDayInput,
} from './promptTemplates';


// ── Types ──────────────────────────────────────────────────────────────────

export type AIErrorCode =
  | 'disabled'
  | 'rate_limited'
  | 'timeout'
  | 'network'
  | 'auth'
  | 'membership'
  | 'invalid_input'
  | 'unsafe_output'
  | 'server_error'
  | 'unknown';

export type AIResult<T> =
  | { ok: true; data: T; tokensUsed?: number; durationMs: number }
  | { ok: false; code: AIErrorCode; message: string; durationMs: number };

export type StoryResult = { story: string };
export type TitleResult = { title: string };
export type TagsResult = { tags: string[] };
export type ToneResult = { tone: string };
export type TimelineResult = { summary: string };
export type OnThisDayResult = { recap: string };

// ── Client-side guards (soft; server has its own hard limits) ──────────────

const RATE_WINDOW_MS = 60_000; // 1 minute
const MAX_PER_WINDOW: Record<AIAction, number> = {
  story: 8,
  title: 12,
  tags: 12,
  tone: 12,
  timeline: 4,
  on_this_day: 4,
};
const REQUEST_TIMEOUT_MS = 25_000;

const inflightTimestamps: Record<AIAction, number[]> = {
  story: [],
  title: [],
  tags: [],
  tone: [],
  timeline: [],
  on_this_day: [],
};

const rateOk = (action: AIAction): boolean => {
  const now = Date.now();
  const arr = inflightTimestamps[action];
  // prune
  while (arr.length && now - arr[0] > RATE_WINDOW_MS) arr.shift();
  if (arr.length >= MAX_PER_WINDOW[action]) return false;
  arr.push(now);
  return true;
};

// ── Usage log (local only — for AIDiagnosticsCard) ─────────────────────────

const USAGE_LS_KEY = 'kinscircle.ai.usage.log.v1';
const USAGE_MAX = 100;

export type AIUsageEntry = {
  id: string;
  ts: number;
  action: AIAction;
  ok: boolean;
  code?: AIErrorCode;
  durationMs: number;
  tokensUsed?: number;
};

const readUsage = (): AIUsageEntry[] => {
  try {
    const raw = localStorage.getItem(USAGE_LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, USAGE_MAX) : [];
  } catch {
    return [];
  }
};

const writeUsage = (next: AIUsageEntry[]) => {
  try {
    localStorage.setItem(USAGE_LS_KEY, JSON.stringify(next.slice(0, USAGE_MAX)));
  } catch {
    /* non-fatal */
  }
};

const recordUsage = (entry: Omit<AIUsageEntry, 'id' | 'ts'>) => {
  const cur = readUsage();
  const next: AIUsageEntry[] = [
    { ...entry, id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, ts: Date.now() },
    ...cur,
  ].slice(0, USAGE_MAX);
  writeUsage(next);
};

export const readAIUsageLog = (): AIUsageEntry[] => readUsage();
export const clearAIUsageLog = (): void => writeUsage([]);

// ── Core invoke ────────────────────────────────────────────────────────────

const invokeAction = async <T>(
  action: AIAction,
  payload: MemoryDraftInput | TimelineInput | OnThisDayInput,
): Promise<AIResult<T>> => {
  const started = Date.now();

  // Phase 6B — soft per-plan quota check (advisory). Resolves to allowed=true
  // when the usageLimits flag is OFF. We don't have role/plan in this layer,
  // so the modal is the layer that performs the role/plan check before
  // calling generateXxx; here we simply enforce the bucket-level cap (no
  // role context). The modal-level check is the authoritative gate.
  const quota = checkAIQuota(action, null, null);
  if (!quota.allowed) {
    const r: AIResult<T> = {
      ok: false,
      code: 'rate_limited',
      message: quota.message,
      durationMs: Date.now() - started,
    };
    recordUsage({ action, ok: false, code: r.code, durationMs: r.durationMs });
    return r;
  }

  if (!rateOk(action)) {
    const r: AIResult<T> = {
      ok: false,
      code: 'rate_limited',
      message: 'You\'re going a little fast. Try again in a moment.',
      durationMs: Date.now() - started,
    };
    recordUsage({ action, ok: false, code: r.code, durationMs: r.durationMs });
    return r;
  }


  // Hard timeout
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<AIResult<T>>((resolve) => {
    timeoutId = setTimeout(() => {
      resolve({
        ok: false,
        code: 'timeout',
        message: 'The AI took too long to respond. Please try again.',
        durationMs: Date.now() - started,
      });
    }, REQUEST_TIMEOUT_MS);
  });

  const callPromise = (async (): Promise<AIResult<T>> => {
    try {
      const { data, error } = await supabase.functions.invoke('ai-memory-assistant', {
        body: { action, payload },
      });
      if (error) {
        return {
          ok: false,
          code: 'network',
          message: error.message || 'Network error.',
          durationMs: Date.now() - started,
        };
      }
      const env = (data ?? {}) as {
        ok?: boolean;
        code?: AIErrorCode;
        message?: string;
        data?: T;
        tokensUsed?: number;
      };
      if (env.ok && env.data) {
        return {
          ok: true,
          data: env.data,
          tokensUsed: env.tokensUsed,
          durationMs: Date.now() - started,
        };
      }
      return {
        ok: false,
        code: env.code ?? 'server_error',
        message: env.message || 'AI service did not return output.',
        durationMs: Date.now() - started,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown AI error.';
      return {
        ok: false,
        code: 'unknown',
        message,
        durationMs: Date.now() - started,
      };
    }
  })();

  const result = await Promise.race([callPromise, timeoutPromise]);
  if (timeoutId) clearTimeout(timeoutId);

  recordUsage({
    action,
    ok: result.ok,
    code: result.ok ? undefined : result.code,
    durationMs: result.durationMs,
    tokensUsed: result.ok ? result.tokensUsed : undefined,
  });

  // Phase 6B — record successful generations against the per-plan quota.
  if (result.ok) recordAIQuotaUsage(action);

  return result;
};


// ── Public methods ─────────────────────────────────────────────────────────

export const generateMemoryStory = async (
  input: MemoryDraftInput,
): Promise<AIResult<StoryResult>> => {
  if (!isAIMemoryWriterEnabled()) {
    return {
      ok: false,
      code: 'disabled',
      message: 'AI Memory Writer is not enabled.',
      durationMs: 0,
    };
  }
  return invokeAction<StoryResult>('story', input);
};

export const generateMemoryTitle = async (
  input: MemoryDraftInput,
): Promise<AIResult<TitleResult>> => {
  if (!isAIMemoryWriterEnabled()) {
    return {
      ok: false,
      code: 'disabled',
      message: 'AI Memory Writer is not enabled.',
      durationMs: 0,
    };
  }
  return invokeAction<TitleResult>('title', input);
};

export const generateMemoryTags = async (
  input: MemoryDraftInput,
): Promise<AIResult<TagsResult>> => {
  if (!isAIMemoryTagsEnabled()) {
    return {
      ok: false,
      code: 'disabled',
      message: 'AI Memory Tags is not enabled.',
      durationMs: 0,
    };
  }
  return invokeAction<TagsResult>('tags', input);
};

export const classifyMemoryTone = async (
  input: MemoryDraftInput,
): Promise<AIResult<ToneResult>> => {
  if (!isAIMemoryWriterEnabled()) {
    return {
      ok: false,
      code: 'disabled',
      message: 'AI Memory Writer is not enabled.',
      durationMs: 0,
    };
  }
  return invokeAction<ToneResult>('tone', input);
};

export const generateTimelineSummary = async (
  input: TimelineInput,
): Promise<AIResult<TimelineResult>> => {
  if (!isAITimelineEnabled()) {
    return {
      ok: false,
      code: 'disabled',
      message: 'AI Timeline Summary is not enabled.',
      durationMs: 0,
    };
  }
  return invokeAction<TimelineResult>('timeline', input);
};

export const generateOnThisDaySummary = async (
  input: OnThisDayInput,
): Promise<AIResult<OnThisDayResult>> => {
  if (!isAIOnThisDayEnabled()) {
    return {
      ok: false,
      code: 'disabled',
      message: 'AI On This Day is not enabled.',
      durationMs: 0,
    };
  }
  return invokeAction<OnThisDayResult>('on_this_day', input);
};
